import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { FullPageLoader } from "@/components/fullpage-loader";
import { usePageTitle } from "@/hooks/usePageTitle";

import { ALPHA_MOCKED_STYLES } from "../generation/alphaMockedStyles";
import { useAlphaRunsQuery } from "../hooks/useAlphaRunsQuery";
import { SafeZoneOverlay, SafeZoneToggle } from "../SafeZoneOverlay";

export function AlphaPreviewPage() {
  const params = useParams({ strict: false }) as {
    designId?: string;
    requestId?: number | string;
  };

  return (
    <AlphaPreview
      designId={params.designId ?? ""}
      requestId={Number(params.requestId)}
    />
  );
}

export function AlphaPreview({
  designId,
  requestId,
}: {
  designId: string;
  requestId: number;
}) {
  const navigate = useNavigate();
  const runsQuery = useAlphaRunsQuery(requestId);
  const [showSafeZones, setShowSafeZones] = useState(true);

  const items = (runsQuery.data?.runs ?? []).flatMap((run) =>
    run.designs.flatMap((design) =>
      design.imageUrl ? [{ design, imageUrl: design.imageUrl, run }] : [],
    ),
  );
  const index = items.findIndex((item) => item.design.designId === designId);
  const item = items[index];

  const goToDesign = (targetIndex: number) =>
    void navigate({
      params: { designId: items[targetIndex]!.design.designId, requestId },
      replace: true,
      to: "/requests/$requestId/alpha/designs/$designId",
    });
  const step = (delta: number) =>
    goToDesign((index + delta + items.length) % items.length);

  usePageTitle(`Preview - #${requestId}`);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!item) return;
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  });

  if (runsQuery.isPending) return <FullPageLoader />;

  if (!item) {
    return (
      <p className="p-xl text-sm text-text-error-primary">
        Could not find this design.
      </p>
    );
  }

  const { design, imageUrl, run } = item;
  const styleTag =
    ALPHA_MOCKED_STYLES[design.planIndex % ALPHA_MOCKED_STYLES.length]!;
  const subtitle = run.format
    ? `${styleTag} · ${run.format.placement}`
    : styleTag;
  const hasSiblings = items.length > 1;

  return (
    <div className="flex flex-col rounded-2xl border border-border-secondary bg-bg-primary">
      <div className="flex items-center justify-between border-b border-border-secondary px-3xl py-xl">
        <div className="flex items-center gap-lg">
          <Button
            color="gray"
            isIconOnly
            onPress={() =>
              void navigate({
                params: { requestId },
                to: "/requests/$requestId/alpha",
              })
            }
            variant="secondary"
          >
            <AccessibleIcon aria-label="Back to all ads">
              <ChevronLeft />
            </AccessibleIcon>
          </Button>
          <div className="flex flex-col">
            <h1 className="text-xl font-extrabold text-text-primary">
              Ad {index + 1} of {items.length}
            </h1>
            <p className="text-sm font-medium text-text-tertiary">{subtitle}</p>
          </div>
        </div>
        <Button
          onPress={() =>
            void navigate({
              params: { designId, requestId },
              to: "/requests/$requestId/alpha/designs/$designId/edit",
            })
          }
          size="xl"
          variant="primary"
        >
          <Pencil size={18} />
          Edit
        </Button>
      </div>
      <div className="flex flex-col gap-xl bg-bg-secondary px-3xl py-xl">
        <div className="flex h-11 items-center justify-end gap-lg">
          {run.format && (
            <>
              <span className="text-sm font-semibold text-text-error-primary">
                Red areas can be covered or cropped
              </span>
              <SafeZoneToggle
                isSelected={showSafeZones}
                onChange={setShowSafeZones}
              />
            </>
          )}
        </div>
        <div className="flex items-center justify-center gap-3xl">
          <NavButton
            direction="previous"
            isHidden={!hasSiblings}
            onPress={() => step(-1)}
          />
          <div className="relative">
            <img
              alt={`Ad ${index + 1}`}
              className="block max-h-[640px] max-w-full shadow-xl"
              src={imageUrl}
            />
            {run.format && showSafeZones && (
              <SafeZoneOverlay safeZone={run.format.safeZone} />
            )}
          </div>
          <NavButton
            direction="next"
            isHidden={!hasSiblings}
            onPress={() => step(1)}
          />
        </div>
        <div className="flex flex-wrap justify-center gap-md pb-md">
          {items.map(
            ({ design: { designId: id }, imageUrl: url }, position) => (
              <Button
                aria-current={id === designId}
                className={twMerge(
                  "size-14 overflow-hidden rounded-lg border-2 border-transparent bg-bg-primary p-xxs opacity-75",
                  id === designId && "border-border-brand opacity-100",
                )}
                key={id}
                onPress={() => goToDesign(position)}
                variant="unstyled"
              >
                <img
                  alt={`View ad ${position + 1}`}
                  className="size-full rounded-sm object-cover"
                  src={url}
                />
              </Button>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

function NavButton({
  direction,
  isHidden,
  onPress,
}: {
  direction: "next" | "previous";
  isHidden: boolean;
  onPress: () => void;
}) {
  const Icon = direction === "next" ? ChevronRight : ChevronLeft;

  return (
    <Button
      className={twMerge(
        "size-13 shrink-0 rounded-full bg-bg-primary shadow-md",
        isHidden && "invisible",
      )}
      color="gray"
      isIconOnly
      onPress={onPress}
      variant="unstyled"
    >
      <AccessibleIcon
        aria-label={direction === "next" ? "Next ad" : "Previous ad"}
      >
        <Icon />
      </AccessibleIcon>
    </Button>
  );
}
